import { Action, createAction, handleActions } from "redux-actions";
import {
  GetOficinasResponse,
  GetSolicitudesEmisionResponse,
  OficinaResponse,
  SolicitudEmision,
} from "~api/types";
import { OptionKV } from "~styled/selector/Selector";
import {
  Dispatch,
  FechaFiltro,
  FiltroValue,
  GetState,
  Services,
  ServicesOficinas,
  TakeValue,
} from "../types";

/**
 * `solicitudesEmision` queda en null al reinicializar la bandeja: es el estado
 * previo a la primera respuesta, distinto de "no hay resultados".
 */
interface SolicitudesEmisionState {
  isLoading: boolean;
  solicitudesEmision: SolicitudEmision[] | null;
  totalRegistros: number;
  oficinasOptions: Record<string, string> | null;
  oficinasFilter: OptionKV<FiltroValue>[];
  isPageAlreadyLoad?: boolean;
  skip?: number;
  take?: TakeValue;
}

const setInitialState = createAction("SET_SOLICITUDESEMISION_INITIAL_STATE");

const getSolicitudesEmisionInited = createAction(
  "OBTENER_SOLICITUDESEMISION_INITED"
);

const getSolicitudesEmisionCompleted = createAction(
  "OBTENER_SOLICITUDESEMISION_COMPLETED",
  (response: GetSolicitudesEmisionResponse | null | undefined) => {
    let solicitudesEmision: SolicitudEmision[] = [];
    let totalRegistros = 0;

    if (response != null) {
      solicitudesEmision = response.solicitudesEmision;
      totalRegistros = response.totalRegistros;
    }

    return { solicitudesEmision, totalRegistros };
  }
);

const getSolicitudesInicioCompleted = createAction(
  "OBTENER_SOLICITUDESINICIO_COMPLETED",
  (response: GetSolicitudesEmisionResponse | null | undefined) => {
    let totalRegistros = 0;

    if (response != null) {
      totalRegistros = response.totalRegistros;
    }

    return { totalRegistros };
  }
);

const getSolicitudesEmisionFailed = createAction(
  "OBTENER_SOLICITUDESEMISION_FAILED",
  (error: unknown) => ({
    error,
  })
);

/**
 * `pestania` decide qué acción se despacha con la respuesta: "solicitud" viene
 * de SOLICITUDES PENDIENTES e "inicio" de INICIO, que comparten endpoint pero
 * escriben distinto el mismo slice.
 */
const getSolicitudesEmision = (
  pestania: string,
  skip: number,
  take: TakeValue,
  searchCriteria?: string | null,
  fechaDesde?: FechaFiltro,
  fechaHasta?: FechaFiltro,
  estado?: FiltroValue | FiltroValue[] | null,
  negocio?: FiltroValue | null,
  oficina?: FiltroValue | null,
  dniCliente?: string | null
) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getSolicitudesEmisionInited());

      const estados = Array.isArray(estado)
        ? estado.map(String)
        : estado
          ? [String(estado)]
          : undefined;

      const data = {
        skip: skip,
        take: Number(take),
        searchCriteria: searchCriteria ?? undefined,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
        estado: estados,
        negocio: negocio ? String(negocio) : undefined,
        dniCliente: dniCliente ?? undefined,
        oficina: oficina ? String(oficina) : undefined,
      };

      const response = await services.api
        .solicitudesEmision()
        .getSolicitudesEmision(data);

      pestania === "solicitud" // "solicitud" es desde pestania SOLICITUDES PENDIENTES, "inicio" desde INICIO ya que colisionan los endpoints
        ? dispatch(getSolicitudesEmisionCompleted(response))
        : dispatch(getSolicitudesInicioCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getSolicitudesEmisionFailed(error));
    }
  };
};

const getOficinasInited = createAction("OBTENER_OFICINAS_INITED");

const getOficinas = () => {
  return async (
    dispatch: Dispatch,
    getState: GetState,
    services: ServicesOficinas
  ) => {
    try {
      dispatch(getOficinasInited());

      const response = await services.api.oficinas().getOficinas();

      dispatch(getOficinasCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getOficinasFailed(error));
    }
  };
};

const getOficinasCompleted = createAction(
  "OBTENER_OFICINAS_COMPLETED",
  (response: GetOficinasResponse | null | undefined) => {
    let oficinas: OficinaResponse[] = [];

    if (response != null) {
      oficinas = response.oficinas;
    }

    return { oficinas };
  }
);

const getOficinasFailed = createAction(
  "OBTENER_OFICINAS_FAILED",
  (error: unknown) => ({
    error,
  })
);

const solicitudesEmisionInitialState: SolicitudesEmisionState = {
  isLoading: false,
  solicitudesEmision: [],
  totalRegistros: 0,
  oficinasOptions: null,
  oficinasFilter: [],
};

const solicitudesEmisionReducer = handleActions<SolicitudesEmisionState, any>(
  {
    [setInitialState.toString()]: (): SolicitudesEmisionState => {
      return {
        ...solicitudesEmisionInitialState,
        solicitudesEmision: null,
        isLoading: false,
      };
    },
    [getSolicitudesEmisionInited.toString()]: (
      state: SolicitudesEmisionState
    ): SolicitudesEmisionState => {
      return {
        ...state,
        totalRegistros: 0,
        isLoading: true,
      };
    },
    [getSolicitudesEmisionCompleted.toString()]: (
      state: SolicitudesEmisionState,
      action: Action<{
        solicitudesEmision: SolicitudEmision[];
        totalRegistros: number;
      }>
    ): SolicitudesEmisionState => {
      return {
        ...state,
        solicitudesEmision: action.payload.solicitudesEmision,
        totalRegistros: action.payload.totalRegistros,
        isPageAlreadyLoad: false,
        isLoading: false,
        skip: Number(state.skip) + Number(state.take),
      };
    },
    [getSolicitudesInicioCompleted.toString()]: (
      state: SolicitudesEmisionState,
      action: Action<{ totalRegistros: number }>
    ): SolicitudesEmisionState => {
      return {
        ...state,
        totalRegistros: action.payload.totalRegistros,
        isLoading: false,
      };
    },
    [getSolicitudesEmisionFailed.toString()]: (
      state: SolicitudesEmisionState
    ): SolicitudesEmisionState => {
      return {
        ...state,
        isPageAlreadyLoad: false,
        isLoading: false,
      };
    },
    [getOficinasInited.toString()]: (
      state: SolicitudesEmisionState
    ): SolicitudesEmisionState => {
      return {
        ...state,
      };
    },
    [getOficinasCompleted.toString()]: (
      state: SolicitudesEmisionState,
      action: Action<{ oficinas: OficinaResponse[] }>
    ): SolicitudesEmisionState => {
      let oficinasFilter: OptionKV<FiltroValue>[] = action.payload.oficinas.map(
        (oficina) => ({
          label: oficina.nombre,
          value: oficina.id,
        })
      );

      oficinasFilter.push({ label: "Todas", value: "" });

      let oficinasOptions: Record<string, string> = {};
      oficinasOptions[""] = "Todas";

      action.payload.oficinas.forEach((oficina) => {
        oficinasOptions[oficina.id] = oficina.nombre;
      });

      return {
        ...state,
        oficinasFilter: oficinasFilter,
        oficinasOptions: oficinasOptions,
      };
    },
    [getOficinasFailed.toString()]: (
      state: SolicitudesEmisionState
    ): SolicitudesEmisionState => {
      return {
        ...state,
      };
    },
  },
  solicitudesEmisionInitialState
);

export { solicitudesEmisionReducer };
export { getSolicitudesEmision, setInitialState, getOficinas };
